import { isPlatformBrowser } from '@angular/common';
import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import {
  rankUnlinkedCandidatesCore,
  toUnlinkedMatchPayload,
  UnlinkedCandidateRow,
  UnlinkedMatchPatientPayload,
  UnlinkedMatchSubject
} from '../unlinked-match/unlinked-candidate-match-core';

export type {
  UnlinkedCandidateBand,
  UnlinkedCandidateDetail,
  UnlinkedCandidateKind,
  UnlinkedCandidateRow,
  UnlinkedMatchPatientPayload,
  UnlinkedMatchSubject
} from '../unlinked-match/unlinked-candidate-match-core';

interface PendingRequest {
  resolve: (rows: UnlinkedCandidateRow[]) => void;
  subject: UnlinkedMatchSubject;
  patients: UnlinkedMatchPatientPayload[];
}

/**
 * Ranks in-memory patients as candidates for one unlinked pathology result.
 *
 * Runs in a Web Worker where one is available, with a synchronous main-thread fallback for
 * SSR and for browsers/webviews without Worker support - the same shape as
 * `PatientDuplicateMatchService`.
 *
 * The result is a ranked list of SUGGESTIONS. This service never links anything.
 */
@Injectable({
  providedIn: 'root'
})
export class UnlinkedCandidateMatchService {
  private worker: Worker | null = null;
  private requestSeq = 0;
  private pending = new Map<number, PendingRequest>();

  constructor(@Inject(PLATFORM_ID) private platformId: object) { }

  /** Synchronous compute (identical rules to the worker). Used for tests and as the fallback. */
  rankCandidatesSync(
    subject: UnlinkedMatchSubject | null | undefined,
    patients: any[] | null | undefined,
  ): UnlinkedCandidateRow[] {
    return rankUnlinkedCandidatesCore(subject, this.toSlim(patients));
  }

  rankCandidates(
    subject: UnlinkedMatchSubject | null | undefined,
    patients: any[] | null | undefined,
  ): Promise<UnlinkedCandidateRow[]> {
    const subj: UnlinkedMatchSubject = subject ?? {};
    const slim = this.toSlim(patients);

    const canUseWorker = isPlatformBrowser(this.platformId) && typeof Worker !== 'undefined';
    if (!canUseWorker) {
      return Promise.resolve(rankUnlinkedCandidatesCore(subj, slim));
    }

    try {
      const worker = this.getWorker();
      const requestId = ++this.requestSeq;
      return new Promise<UnlinkedCandidateRow[]>(resolve => {
        this.pending.set(requestId, { resolve, subject: subj, patients: slim });
        worker.postMessage({ requestId, subject: subj, patients: slim });
      });
    } catch {
      return Promise.resolve(rankUnlinkedCandidatesCore(subj, slim));
    }
  }

  private toSlim(patients: any[] | null | undefined): UnlinkedMatchPatientPayload[] {
    return (patients ?? [])
      .filter(p => p?.active === true)
      .map(toUnlinkedMatchPayload);
  }

  private getWorker(): Worker {
    if (!this.worker) {
      const worker = new Worker(
        new URL('../unlinked-match/unlinked-candidate-match.worker', import.meta.url),
        { type: 'module' }
      );
      worker.onmessage = (ev: MessageEvent<{ requestId: number; rows: UnlinkedCandidateRow[] }>) => {
        const data = ev.data;
        const entry = this.pending.get(data?.requestId);
        if (!entry) {
          return;
        }
        this.pending.delete(data.requestId);
        entry.resolve(data.rows ?? []);
      };
      worker.onerror = () => {
        // Worker died: finish every outstanding request on the main thread rather than hang.
        const entries = Array.from(this.pending.values());
        this.pending.clear();
        this.worker = null;
        for (const entry of entries) {
          entry.resolve(rankUnlinkedCandidatesCore(entry.subject, entry.patients));
        }
      };
      this.worker = worker;
    }
    return this.worker;
  }
}
